import { cn } from "cn";
import type { HTMLAttributes } from "react";
import { markdownToHtml } from "@/components/ui/editor/core";

/** Styling every rendering shares — the inline run markdown can produce. */
const INLINE_STYLES = cn(
  /* sorted */
  "wrap-break-words",
  "[&_a]:text-text-primary [&_a]:break-all [&_a]:underline",
  "[&_span[data-type='mention']]:font-medium [&_span[data-type='mention']]:text-text-primary",
);

/** Styling for the elements only a BLOCK rendering emits; `inline` produces none. */
const BLOCK_STYLES = cn(
  /* sorted */
  "[&_ol]:list-decimal [&_ol]:px-5 [&_ol]:my-5 [&_ol]:first-of-type:mt-0 [&_ol]:last-of-type:mb-0",
  "[&_ul]:list-disc [&_ul]:px-5 [&_ul]:my-5 [&_ul]:first-of-type:mt-0 [&_ul]:last-of-type:mb-0",
  "[&_p]:min-h-5.5 [&_p]:my-3 [&_p]:first-of-type:mt-0 [&_p]:last-of-type:mb-0",
);

export type MarkdownProps = Omit<
  HTMLAttributes<HTMLElement>,
  "children" | "dangerouslySetInnerHTML"
> & {
  content?: string;
  /**
   * Draw the markdown as part of the CALLER's line instead of as its own block: a
   * `<span>` of inline content, with no `<p>` and no list around it.
   *
   * For a summary rendered inside a line of the caller's own text — an inbox row's
   * preview, where the author prefix and a glyph share the line with it. The block
   * rendering cannot live there: a `<p>` inside the caller's `<p>` is invalid nesting,
   * and `text-overflow: ellipsis` drops an overflowing block-level box whole rather
   * than clipping it, so a truncating line renders as nothing but `…`.
   */
  inline?: boolean;
};

export function Markdown({
  content = "",
  inline,
  className,
  ...props
}: MarkdownProps) {
  if (!content) return null;

  const html = { __html: markdownToHtml(content, { inline }) };

  if (inline) {
    return (
      <span
        className={cn(/* sorted */ INLINE_STYLES, className)}
        dangerouslySetInnerHTML={html}
        {...props}
      />
    );
  }

  return (
    <div
      className={cn(/* sorted */ INLINE_STYLES, BLOCK_STYLES, className)}
      dangerouslySetInnerHTML={html}
      {...props}
    />
  );
}
